<template>
  <div>
    <ButtonGroup size="small">
      <Button :disabled="addNew" type="primary" @click="handleAdd">
        新增
      </Button>
    </ButtonGroup>
    <ButtonGroup size="small" class="ivu-ml-8">
      <Button
        type="success"
        :disabled="selectedIds.length < 1"
        @click="handleEnableMultiple"
      >
        启用
      </Button>
      <Button
        type="warning"
        :disabled="selectedIds.length < 1"
        @click="handleDisableMultiple"
      >
        禁用
      </Button>
      <Button
        @click="handleDeleteMultiple"
        type="error"
        :disabled="selectedIds.length < 1"
      >
        删除
      </Button>
    </ButtonGroup>

    <div class="ivu-inline-block ivu-fr">
      <Tooltip
        class="ivu-ml"
        :content="tableFullscreen ? '退出全屏' : '全屏'"
        placement="top"
      >
        <i-link @click.native="handleFullscreen">
          <Icon
            :custom="
              tableFullscreen
                ? 'i-icon i-icon-exit-full-screen'
                : 'i-icon i-icon-full-screen'
            "
          />
        </i-link>
      </Tooltip>
      <Tooltip class="ivu-ml" content="刷新" placement="top">
        <i-link @click.native="handleRefresh">
          <Icon custom="i-icon i-icon-refresh" />
        </i-link>
      </Tooltip>
      <Dropdown trigger="click">
        <Tooltip class="ivu-ml" content="列设置" placement="top">
          <i-link>
            <Icon type="md-options" />
          </i-link>
        </Tooltip>
        <DropdownMenu slot="list">
          <div class="ivu-p-8">列展示</div>
          <Divider size="small" class="ivu-mt-8 ivu-mb-8" />
          <template v-for="item in columns">
            <li
              class="ivu-dropdown-item"
              :key="item.title"
              v-if="item.title"
              @click="item.show = !item.show"
            >
              <Checkbox v-model="item.show"></Checkbox>
              <span>{{ item.title }}</span>
            </li>
          </template>
        </DropdownMenu>
      </Dropdown>
    </div>

    <Alert show-icon class="ivu-mt">
      <p>字幕文件访问规则与视频文件访问规则相同, 只是后缀不同</p>
      <p>字幕文件需要跟 m3u8 文件放在同一个目录下, 如:</p>
      <p>
        有视频路径为 xxxxxx.mp4/index.m3u8, 则字幕文件路径为
        xxxxxx.mp4/[lang].vtt, 其中 [lang] 是语言名称占位符
      </p>
      <p>网页播放器目前只支持 webvtt 格式的字幕, 即 .vtt 后缀的文件</p>
    </Alert>

    <Table
      ref="table"
      :columns="tableColumns"
      :data="dataList"
      :loading="loading"
      class="ivu-mt"
      @on-select="handleSelect"
      @on-select-cancel="handleSelectCancel"
      @on-select-all="handleSelectAll"
      @on-select-all-cancel="handleClearSelect"
    >
      <template slot-scope="{ row, index }" slot="name">
        <Input
          v-if="editIndex === index"
          type="text"
          size="small"
          v-model="editInfo.name"
        />
        <span v-else>{{ row.name }}</span>
      </template>

      <template slot-scope="{ row, index }" slot="prefix">
        <template v-if="editIndex === index">
          <Select
            v-model="editInfo.scheme"
            size="small"
            placeholder="协议"
            v-width="80"
            transfer
          >
            <Option :value="1">http://</Option>
            <Option :value="2">https://</Option>
          </Select>
          <Input
            v-model="editInfo.host"
            size="small"
            placeholder="主机"
            v-width="175"
          />
          :
          <Input
            type="text"
            v-model="editInfo.port"
            size="small"
            placeholder="端口"
            v-width="55"
            maxlength="5"
          />
          <Input
            type="text"
            v-model="editInfo.base_url"
            size="small"
            placeholder="路径"
            v-width="150"
          />
          /[文件名][相应后缀]
        </template>
        <div v-else>
          <div>
            <Tag color="blue">视频</Tag>
            {{ schemeLabel(row.scheme) }}{{ row.host
            }}{{ portLabel(row.port, row.scheme) }}{{ row.base_url }}/[文件名]{{
              row.video_suffix
            }}
          </div>
          <div>
            <Tag color="orange">字幕</Tag>
            {{ schemeLabel(row.scheme) }}{{ row.host
            }}{{ portLabel(row.port, row.scheme) }}{{ row.base_url }}/[文件名]{{
              row.subtitle_suffix
            }}
          </div>
        </div>
      </template>

      <template slot-scope="{ row, index }" slot="video_suffix">
        <Input
          v-if="editIndex === index"
          type="text"
          v-model="editInfo.video_suffix"
          size="small"
          placeholder="视频后缀"
        />
        <template v-else>{{ row.video_suffix }}</template>
      </template>

      <template slot-scope="{ row, index }" slot="subtitle_suffix">
        <Input
          v-if="editIndex === index"
          type="text"
          v-model="editInfo.subtitle_suffix"
          size="small"
          placeholder="字幕后缀"
        />
        <template v-else>{{ row.subtitle_suffix }}</template>
      </template>

      <template slot-scope="{ row, index }" slot="status">
        <i-switch
          v-if="editIndex === index"
          size="small"
          v-model="editInfo.status"
          :true-value="1"
          :false-value="0"
        />
        <template v-else>
          <Badge v-if="row.status === 1" color="green" text="正常" />
          <Badge v-else-if="row.status === 0" color="yellow" text="禁用" />
        </template>
      </template>

      <template slot-scope="{ row, index }" slot="action">
        <template v-if="editIndex === index">
          <ButtonGroup size="small">
            <Button type="success" ghost @click="handleSave(index)">
              <template v-if="!addNew">保存</template>
              <template v-else>添加</template>
            </Button>
            <Button v-if="!addNew" type="primary" ghost @click="handleCancel">
              取消
            </Button>
            <Button
              v-else
              @click="handleCancelAdd(index)"
              type="error"
              ghost
              size="small"
            >
              删除
            </Button>
          </ButtonGroup>
        </template>
        <template v-else>
          <ButtonGroup size="small">
            <Button type="primary" ghost @click="handleEdit(row, index)">
              编辑
            </Button>
            <Button type="error" ghost @click="handleDelete(row.id)">
              删除
            </Button>
          </ButtonGroup>
        </template>
      </template>
    </Table>

    <div class="ivu-mt ivu-text-center">
      <Page
        size="small"
        :total="dataTotal"
        :current.sync="page"
        :page-size-opts="[10, 20, 50, 100]"
        show-total
        show-sizer
        show-elevator
        :page-size="pagesize"
        @on-change="handleChangePage"
        @on-page-size-change="handleChangePageSize"
      />
    </div>
  </div>
</template>

<script>
import { datetimeFormat } from '@/utils/datetime'
import api from '@/api'
// import { cloneDeep } from 'lodash'
import tableColumns from './table-columns'
import dataListMixin from '@/mixins/data-list'

export default {
  data() {
    return {
      datetimeFormat,
      editIndex: -1,
      addNew: false,
      editInfo: {
        name: '',
        scheme: '',
        host: '',
        port: '',
        base_url: '',
        video_suffix: '',
        subtitle_suffix: '',
        status: 1,
      },

      columns: tableColumns,

      dataInfoRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
        ],
      },
    }
  },
  mixins: [dataListMixin],
  methods: {
    schemeLabel(scheme) {
      if (scheme === 1) {
        return 'http://'
      } else if (scheme === 2) {
        return 'https://'
      } else {
        return ''
      }
    },
    portLabel(port, scheme) {
      if (port === 0) {
        return ''
      }
      if ((scheme === 1 && port !== 80) || (scheme === 2 && port !== 443)) {
        return ':' + port
      }
      return ''
    },
    // 取数据列表
    handleGetDataList() {
      if (this.loading) return
      this.loading = true

      // 下面的 params 是获取的表单查询参数
      const { keyword, status, page } = this.$parent.$parent.$refs.filter.params
      if (page) {
        this.page = page
        this.$parent.$parent.$refs.filter.params.page = null
      }
      api.videoServer
        .list({
          keyword,
          status,
          page: this.page, // 页数
          pagesize: this.pagesize, // 每页数量
        })
        .then((data) => {
          if (!data) return

          this.handleClearSelect()
          this.dataList = data.video_server_list || []
          this.dataTotal = data.video_server_total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    // 取单一数据
    handleGetDataInfo(id) {
      if (this.loading) return
      this.loading = true

      // eslint-disable-next-line
      return new Promise((resolve, reject) => {
        api.videoServer
          .info({
            id,
          })
          .then((data) => {
            if (!data) return

            this.dataInfo = data.video_server || null

            resolve()
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false
          })
      })
    },
    // 批量启用
    handleEnableMultiple() {
      if (this.loading) return
      this.loading = true

      const ids = this.ids
      api.videoServer
        .enable({
          ids,
        })
        .then(() => {
          this.loading = false

          this.$Modal.remove()
          this.$Message.success('操作成功')
          // 完成后刷新数据
          // this.handleClearSelect()
          // this.handleChangePage(1)
          this.handleGetDataList()
        })
        .catch(() => {
          this.loading = false
        })
    },
    // 批量禁用
    handleDisableMultiple() {
      if (this.loading) return
      this.loading = true

      const ids = this.ids
      api.videoServer
        .disable({
          ids,
        })
        .then(() => {
          this.loading = false

          this.$Modal.remove()
          this.$Message.success('操作成功')
          // 完成后刷新数据
          // this.handleClearSelect()
          // this.handleChangePage(1)
          this.handleGetDataList()
        })
        .catch(() => {
          this.loading = false
        })
    },

    // 批量删除
    handleDeleteMultiple() {
      const ids = this.ids
      this.$Modal.confirm({
        title: '删除确认',
        content: `删除后数据不可恢复，确定要删除所选数据项？`,
        loading: true,
        onOk: () => {
          if (this.loading) return
          this.loading = true

          api.videoServer
            .delete({
              ids,
            })
            .then(() => {
              this.loading = false

              this.$Modal.remove()
              this.$Message.success('操作成功')
              // 完成后刷新数据
              // this.handleClearSelect()
              // this.handleChangePage(1)
              this.handleGetDataList()
            })
            .catch(() => {
              this.loading = false
            })
        },
      })
    },
    // 单条删除
    handleDelete(id) {
      this.$Modal.confirm({
        title: '删除确认',
        content: `删除后数据不可恢复，确定要删除此项数据？`,
        loading: true,
        onOk: () => {
          if (this.loading) return
          this.loading = true

          api.videoServer
            .delete({
              ids: id,
            })
            .then(() => {
              this.loading = false

              this.$Modal.remove()
              this.$Message.success('操作成功')
              // 完成后刷新数据
              // this.handleClearSelect()
              // this.handleChangePage(1)
              this.handleGetDataList()
            })
            .catch(() => {
              this.loading = false
            })
        },
      })
    },

    // 新增
    handleAdd() {
      this.addNew = true
      const row = {
        id: '',
        name: '',
        scheme: '',
        host: '',
        port: '',
        base_url: '',
        video_suffix: '',
        subtitle_suffix: '',
        status: 1,
      }
      this.dataList.unshift(row)
      this.handleEdit(row, 0)
    },

    // 编辑
    handleEdit(row, index) {
      this.editInfo.id = row.id
      this.editInfo.name = row.name
      this.editInfo.scheme = row.scheme
      this.editInfo.host = row.host
      this.editInfo.port = row.port !== 0 ? row.port : ''
      this.editInfo.base_url = row.base_url
      this.editInfo.video_suffix = row.video_suffix
      this.editInfo.subtitle_suffix = row.subtitle_suffix
      this.editInfo.status = row.status
      this.editIndex = index
    },

    // 保存
    handleSave(index) {
      // if (!this.editName || !this.editId || !this.editDepartment) {
      //   this.$Message.error('请填写完整的成员信息！')
      //   return
      // }

      if (this.loading) return
      if (this.submitting) return
      this.loading = true
      this.submitting = true

      const params = {
        id: this.editInfo.id,
        name: this.editInfo.name,
        scheme: this.editInfo.scheme,
        host: this.editInfo.host,
        port: this.editInfo.port,
        base_url: this.editInfo.base_url,
        video_suffix: this.editInfo.video_suffix,
        subtitle_suffix: this.editInfo.subtitle_suffix,
        status: this.editInfo.status,
      }
      // console.log(params)
      api.videoServer
        .save(params)
        .then(() => {
          this.loading = false

          this.dataList[index].name = this.editInfo.name
          this.dataList[index].scheme = this.editInfo.scheme
          this.dataList[index].host = this.editInfo.host
          this.dataList[index].port = this.editInfo.port
          this.dataList[index].base_url = this.editInfo.base_url
          this.dataList[index].status = this.editInfo.status

          this.editIndex = -1
          this.addNew = false

          this.$Message.success('操作成功')
          this.handleGetDataList()
        })
        .catch(() => {
          this.loading = false
        })
        .finally(() => {
          this.submitting = false
        })
    },

    // 取消编辑
    handleCancel() {
      this.editIndex = -1
      this.addNew = false
    },

    // 取消添加
    handleCancelAdd(index) {
      this.dataList.splice(index, 1)
      this.editIndex = -1
      this.addNew = false
    },
  },
}
</script>
<style lang="scss">
.page-sys-user {
  &-drawer {
    &-footer {
      width: 100%;
      position: absolute;
      bottom: 0;
      left: 0;
      border-top: 1px solid #e8e8e8;
      padding: 10px 16px;
      text-align: right;
      background: #fff;
    }
    &-form {
      .ivu-input[disabled],
      fieldset[disabled] .ivu-input,
      .ivu-select-disabled .ivu-select-selection {
        background-color: #fff;
        color: inherit;
      }
      &-region {
        .i-region-transfer {
          width: 270px;
        }
        .i-region-drop-main {
          width: 270px !important;
          height: 200px;
          overflow: auto;
          column-count: 2;
          &-item {
            line-height: normal;
          }
        }
      }
    }
  }
}
</style>
